import { InfoOutlined } from '@mui/icons-material';
import {
  Box,
  Checkbox,
  IconButton,
  TableHead as MuiTableHead,
  type SxProps,
  TableCell,
  TableRow,
  TableSortLabel,
  type Theme,
  Tooltip,
  useTheme,
} from '@mui/material';
import { visuallyHidden } from '@mui/utils';
import type { Property } from 'csstype';
import {
  Fragment,
  type MouseEvent,
  type ReactElement,
  type Ref,
  forwardRef,
  useCallback,
} from 'react';

import Enums from '@core/types/api/Enums';
import { hasStringValue } from '@core/utils/equality';
import type { NestedKeyOf } from '@core/utils/types';

import type { FilterByOpts, NamedFilterOpts } from '../../../utils/ApiClient';
import { type Column, resolveSortId } from '../TableDataModel';
import { ColumnFilter } from './ColumnFilter';
import type { SortConfig } from './SortHelpers';
import type { TypeAheadTables } from './types';

type EnhancedTableProps<TRow extends { id: string }> = {
  headCells: ReadonlySet<Column<TRow>>;
  numSelected: number;
  typeaheadTableName: TypeAheadTables | undefined;
  tableFilter: FilterByOpts<TRow>[];
  allFilters: FilterByOpts<TRow>[];
  onRequestSort: (event: MouseEvent<unknown>, property: NestedKeyOf<TRow>) => void;
  onSelectAllClick: (value: boolean) => void;
  onColumnFilterChange?: (column: Column<TRow>, filter?: NamedFilterOpts<TRow>) => void;
  onColumnFilterRemove?: (column: Column<TRow>) => void;
  sortConfig: SortConfig<TRow>;
  rowCount: number;
  includeSelect: boolean;
  includeEdit: boolean;
  sx?: SxProps<Theme>;
};

const columnSeparator = (color: Property.Color): ReactElement => (
  <TableCell padding='none' sx={{ textAlign: 'right' }}>
    <Box
      sx={{
        background: color,
        height: '1.5rem',
        width: '2px',
        margin: '0 0.75rem',
        display: 'inline-block',
        verticalAlign: 'middle',
      }}
    />
  </TableCell>
);

const supportedTypes = new Set(['number', 'Cost', 'Currency', 'string', 'DateTime']);
const isSupportedFilter = <TRow extends { id: string }>(column: Column<TRow>): boolean => {
  return supportedTypes.has(column.type) && column.isFilterDisabled !== true;
};

/** The DataTable Head which displays column names + supports sorting */
const TableHeadImpl = <TRow extends { id: string }>(
  props: EnhancedTableProps<TRow>,
  ref: Ref<HTMLTableSectionElement>,
): ReactElement => {
  const theme = useTheme();
  const {
    onSelectAllClick,
    sortConfig,
    numSelected,
    rowCount,
    tableFilter,
    allFilters,
    onRequestSort,
    headCells,
    onColumnFilterChange,
    onColumnFilterRemove,
    sx = {},
  } = props;

  const createSortHandler =
    (property: NestedKeyOf<TRow>) =>
    (event: MouseEvent<unknown>): void => {
      onRequestSort(event, property);
    };

  const columnHeaderContent = (
    headCell: Column<TRow>,
    effectiveSortId: NestedKeyOf<TRow>,
  ): ReactElement => (
    <TableSortLabel
      active={sortConfig.name === effectiveSortId}
      direction={
        sortConfig.name === effectiveSortId
          ? sortConfig.direction
          : (headCell.initialSortOrder ?? Enums.SortDirection.Ascending)
      }
      onClick={createSortHandler(effectiveSortId)}
      disabled={
        typeof headCell.isSortingDisabled === 'function'
          ? headCell.isSortingDisabled(
              allFilters.filter((f) => 'name' in f && f.name === headCell.id),
              allFilters,
            )
          : headCell.isSortingDisabled
      }
    >
      <Box pl='1.3rem' sx={{ display: 'flex', alignItems: 'center' }}>
        {typeof headCell.label === 'function'
          ? headCell.label(allFilters.filter((f) => 'name' in f && f.name === headCell.id))
          : headCell.label}
      </Box>

      {sortConfig.name === effectiveSortId ? (
        <Box component='span' sx={{ ...visuallyHidden }}>
          {sortConfig.direction === Enums.SortDirection.Descending
            ? 'sorted descending'
            : 'sorted ascending'}
        </Box>
      ) : null}
    </TableSortLabel>
  );

  const handleFilterChange = useCallback(
    (column: Column<TRow>, filter: NamedFilterOpts<TRow> | null) => {
      onColumnFilterChange?.(column, filter === null ? undefined : filter);
    },
    [onColumnFilterChange],
  );

  return (
    <MuiTableHead
      ref={ref}
      key='enhanced-table-head'
      sx={{
        position: 'sticky',
        top: 0,
        background: theme.palette.SlabGray.Table,
        zIndex: theme.zIndex.tableHead,
        ...sx,
      }}
    >
      <TableRow id='enhanced-table-head-row' sx={{ zIndex: theme.zIndex.tableRow }}>
        {props.includeSelect && headCells.size > 0 && (
          <Fragment key='select-header'>
            <TableCell
              padding='checkbox'
              sx={{
                position: 'sticky',
                left: 0,
                zIndex: theme.zIndex.tableStickyCell,
                background: 'white',
              }}
            >
              <Checkbox
                sx={{
                  my: 0.5,
                }}
                color='primary'
                indeterminate={numSelected > 0 && numSelected < rowCount}
                checked={rowCount > 0 && numSelected === rowCount}
                onChange={(event) => onSelectAllClick(event.target.checked)}
                inputProps={{
                  'aria-label': 'select all',
                }}
              />
            </TableCell>
          </Fragment>
        )}
        {[...headCells].map((headCell, index) => {
          const associatedFilters = allFilters.filter((f) => 'name' in f && f.name === headCell.id);
          const resolvedTooltipText =
            typeof headCell.tooltipText === 'function'
              ? headCell.tooltipText(associatedFilters)
              : headCell.tooltipText;
          const effectiveSortId = resolveSortId(headCell, associatedFilters, allFilters);
          return (
            <Fragment key={headCell.id}>
              {index !== 0 && columnSeparator(theme.palette.SlabGray['400'])}
              <TableCell
                sortDirection={sortConfig.name === effectiveSortId ? sortConfig.direction : false}
                sx={{ padding: '0.5rem' }}
              >
                <Box width={headCell.width} sx={{ display: 'flex', alignItems: 'center' }}>
                  {columnHeaderContent(headCell, effectiveSortId)}

                  {onColumnFilterChange !== undefined && isSupportedFilter(headCell) && (
                    <ColumnFilter
                      typeaheadTableName={props.typeaheadTableName}
                      column={headCell}
                      filter={tableFilter?.find(
                        (it): it is NamedFilterOpts<TRow> =>
                          'name' in it && it.name === headCell.id,
                      )}
                      onFilterChange={handleFilterChange}
                      onFilterRemove={onColumnFilterRemove}
                    />
                  )}
                  {hasStringValue(resolvedTooltipText) && (
                    <Tooltip title={resolvedTooltipText}>
                      <IconButton
                        aria-label={resolvedTooltipText}
                        disableRipple
                        size='small'
                        sx={{ marginLeft: '0.25rem', padding: 0 }}
                      >
                        <InfoOutlined sx={{ fontSize: 16, color: 'SlabGray.500' }} />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
              </TableCell>
            </Fragment>
          );
        })}
        {props.includeEdit && headCells.size > 0 && (
          <Fragment key='edit-header'>
            <TableCell
              padding='checkbox'
              sx={{
                position: 'sticky',
                right: 0,
                zIndex: theme.zIndex.tableStickyCell,
                background: 'white',
              }}
            />
          </Fragment>
        )}
      </TableRow>
    </MuiTableHead>
  );
};

export const TableHead = forwardRef(TableHeadImpl) as <TRow extends { id: string }>(
  props: EnhancedTableProps<TRow> & { ref?: Ref<HTMLTableSectionElement> },
) => ReactElement;
